import { Component, OnDestroy, OnInit, ElementRef, ViewChild } from '@angular/core';
import { FooterComponent, StepRoute } from '../footer/footer.component';
import { Step, StepperComponent } from '../stepper/stepper.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SessionStorageService } from '../services/session-storage.service';
import { ServicesStateService } from '../services/services-state.service';
import { Subscription, filter } from 'rxjs';
import { AppointmentRequest, Customer, Email, PhoneNumber, Price, Service, Vehicle } from '../services/client.service';
import { AppService } from '../services/app.service';

@Component({
  selector: 'app-book-appointment',
  standalone: true,
  imports: [StepperComponent, FooterComponent, RouterOutlet],
  templateUrl: './book-appointment.component.html',
  styleUrl: './book-appointment.component.scss'
})
export class BookAppointmentComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();
  isNextDisabled = false;
  activeChild: any;
  @ViewChild('contentContainer') contentContainer?: ElementRef<HTMLDivElement>;
  vehicleInformation: string = $localize`Enter Vehicle Information`;
  Services: string = $localize`Select Services`;
  Appointment: string = $localize`Book Appointment`;
  CustomerInformation: string = $localize`Enter Customer Information`;
  Overview: string = $localize`Confirm Appointment`;


  constructor(
    private sessionStorageService: SessionStorageService,
    private servicesStateService: ServicesStateService,
    private router: Router,
    private appService: AppService
  ) {
    this.handleSubmit = this.handleSubmit.bind(this);
    this.currentStepInfo = this.currentStepInfo.bind(this);
    this.shouldDisableBackButton = this.shouldDisableBackButton.bind(this);
    this.validateAndNavigate = this.validateAndNavigate.bind(this);
  }

  ngOnInit(): void {
    const servicesSub = this.servicesStateService.servicesSelected$.subscribe(hasServices => {
      this.updateNextButtonState(hasServices, this.router.url);
    });
    this.subscriptions.add(servicesSub);

    const routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((navigationEvent) => {
        const hasServices = this.servicesStateService.getCurrentState();
        this.updateNextButtonState(hasServices, navigationEvent.url);
      });
    this.subscriptions.add(routerSub);

    const initialUrl = this.router.url;
    const initialHasServices = this.servicesStateService.getCurrentState() || this.hasServicesInSession();
    this.updateNextButtonState(initialHasServices, initialUrl);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public updateNextButtonState(hasServices: boolean, currentUrl: string): void {
    if (currentUrl.includes('book-appointment/service')) {
      const effectiveHasServices = hasServices || this.hasServicesInSession();
      this.isNextDisabled = !effectiveHasServices;
    } else {
      this.isNextDisabled = false;
    }
  }

  public hasServicesInSession(): boolean {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    if (!sessionDataString) return false;
    try {
      const sessionData = JSON.parse(sessionDataString);
      return Array.isArray(sessionData?.appointment?.services) && sessionData.appointment.services.length > 0;
    } catch {
      return false;
    }
  }

  steps: Step[] = [
    {
      id: 1,
      label: this.vehicleInformation,
      route: 'book-appointment/vehicle-info',
      completed: false,
      active: false,
      icon: "directions_car"
    },
    {
      id: 2,
      label: this.Services,
      route: 'book-appointment/service',
      completed: false,
      active: false,
      icon: "build"
    },
    {
      id: 3,
      label: this.Appointment,
      route: 'book-appointment/appointment',
      completed: false,
      active: false,
      icon: "event_available"
    },
    {
      id: 4,
      label: this.CustomerInformation,
      route: 'book-appointment/customer-info',
      completed: false,
      active: false,
      icon: 'person'
    },
    {
      id: 5,
      label: this.Overview,
      route: 'book-appointment/overview',
      completed: false,
      active: false,
      icon: 'check'
    },
  ];

  stepRoutes: StepRoute[] = [
    {
      id: 1,
      route: 'book-appointment/vehicle-info',
      nextRoute: 'book-appointment/service'
    },
    {
      id: 2,
      route: 'book-appointment/service',
      nextRoute: 'book-appointment/appointment',
      prevRoute: 'book-appointment/vehicle-info'
    },
    {
      id: 3,
      route: 'book-appointment/appointment',
      nextRoute: 'book-appointment/customer-info',
      prevRoute: 'book-appointment/service'
    },
    {
      id: 4,
      route: 'book-appointment/customer-info',
      nextRoute: 'book-appointment/overview',
      prevRoute: 'book-appointment/appointment'
    },
    {
      id: 5,
      route: 'book-appointment/overview',
      prevRoute: 'book-appointment/customer-info'
    },
  ];

  currentStepInfo(url: string) {
    return this.stepRoutes.find(step => step.route === url);
  }

  shouldDisableBackButton(url: string): boolean {
    if (url.includes('book-appointment/overview')) {
      return true;
    }
    if (url.includes('book-appointment/customer-info')) {
      const navigationState = history.state;
      return navigationState?.fromOverviewChange === true;
    }

    if (url.includes('book-appointment/service')) {
      this.clearAppointmentDateAndTimeslot();
    }
    return false;
  }

  public clearAppointmentDateAndTimeslot(): void {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    if (!sessionDataString) return;
  
    const sessionData = JSON.parse(sessionDataString);
  
    if (sessionData.appointment) {
      delete sessionData.appointment.appointmentDate;
      delete sessionData.appointment.selectedTimeslot;
    }
  
    this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(sessionData));
  }
  
  handleSubmit(): void {
    this.isNextDisabled = true;
    this.appService.setLoading(true);
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};
    const appointmentDate = sessionData.appointment?.appointmentDate;
    const selectedTimeslot = sessionData.appointment?.selectedTimeslot;

    let timeslotFrom: string | undefined;
    if (appointmentDate && selectedTimeslot) {
      const [startTime] = selectedTimeslot.split(" - ");
      const time24 = this.convertTo24Hour(startTime.trim());
      const serverOffset = sessionData.appointment?.timeslotFromUtcOffset;
      timeslotFrom = `${appointmentDate}T${time24}:00${serverOffset}`;
    }

    const customer = new Customer({
      name: sessionData.customerInfo?.name,
      phone: sessionData.customerInfo?.phoneNo
        ? new PhoneNumber({ phoneNo: sessionData.customerInfo.phoneNo })
        : undefined,
      email: sessionData.customerInfo?.email
        ? new Email({ eMail: sessionData.customerInfo.email })
        : undefined,
    });

    const vehicle = new Vehicle(sessionData.vehicleInfo);
    const paymentMethod = sessionData.customerInfo?.contactType || "";
    const services =
      sessionData.appointment?.services?.map(
        (service: Service) =>
          new Service({
            ...service,
            unitPrice: service.unitPrice ? new Price(service.unitPrice) : undefined,
          })
      ) || [];

    const appointmentRequest = new AppointmentRequest({
      locationId: sessionData.dealership?.id,
      vehicle,
      customer,
      timeslotFrom,
      preferredPaymentMethod: paymentMethod,
      comment: sessionData.customerInfo.note,
      resourceIds: sessionData.appointment?.serviceAdvisorId ? [sessionData.appointment.serviceAdvisorId] : [],
      services,
      dataPrivacyConsent: [],
    });

    this.appService.postAppointment(appointmentRequest).subscribe({
      next: (response: string) => {
        const data = JSON.parse(response);
        const appointmentId = data?.AppointmentId;
        const documentNo = data?.DocumentNo;

        if (appointmentId) {
          const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
          const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};
          sessionData.appointment = {
            ...(sessionData.appointment || {}),
            appointmentId,
            documentNo,
          };
          this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(sessionData));
            this.router.navigate(['/confirmation']).then(() => {
            this.appService.setLoading(false);
          });
        }

      },
      error: (err) => {
        this.appService.handleErrorMessage(err);
        this.appService.setLoading(false);
      },
    });
  }

  public convertTo24Hour(input: string): string {
    const trimmed = input.trim();
    const ampmMatch = /(AM|PM)$/i.test(trimmed);
    if (ampmMatch) {
      const [time, modifierRaw] = trimmed.split(' ');
      let [hours, minutes] = time.split(':');
      const modifier = modifierRaw.toUpperCase();
      if (hours === '12') {
        hours = '00';
      }
      if (modifier === 'PM') {
        hours = (parseInt(hours, 10) + 12).toString();
      }
      return `${hours.padStart(2, '0')}:${minutes}`;
    }
    const parts = trimmed.split(':');
    if (parts.length >= 2) {
      const h = parts[0].padStart(2, '0');
      const m = parts[1].slice(0, 2);
      return `${h}:${m}`;
    }
    return trimmed;
  }

  onActivate(component: any): void {
    this.activeChild = component;
    setTimeout(() => {
      const element = this.contentContainer?.nativeElement;
      if (element) {
        element.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    });
  }

  validateAndNavigate(nextRoute: string): boolean {
    const currentUrl = this.router.url;

    switch (true) {
      case currentUrl.includes('book-appointment/vehicle-info'): {
        const form = this.activeChild?.vehicleInfoForm;
        if (form?.invalid) {
          form.markAllAsTouched();
          return false;
        }
        break;
      }
      case currentUrl.includes('book-appointment/appointment'): {
        if (this.activeChild?.isFormReady && !this.activeChild.isFormReady()) {
          if (this.activeChild?.validateForm && !this.activeChild.validateForm()) {
            return false;
          }
        }
        const form = this.activeChild?.resourcesForm;
        if (form?.invalid) {
          form.markAllAsTouched();
          return false;
        }
        break;
      }
      case currentUrl.includes('book-appointment/customer-info'): {
        const form = this.activeChild?.customerInfoForm;
        if (form) {
          if (typeof this.activeChild?.formSubmitted !== 'undefined') {
            this.activeChild.formSubmitted = true;
          }

          const requiredFields = ['name', 'phoneNo', 'email', 'consent'];
          let hasErrors = false;

          requiredFields.forEach(key => {
            const control = form.get(key);
            if (
              control?.errors?.['required'] ||
              control?.errors?.['email'] ||
              control?.errors?.['pattern']
            ) {
              control.markAsTouched();
              hasErrors = true;
            }
          });

          if (hasErrors) {
            const consentCtrl = form.get('consent');

            if (consentCtrl?.errors?.['required']) {
              setTimeout(() => {
                this.activeChild?.scrollToConsent();
              }, 0);

              return true; 
            }

            return false;
          }
        }
        break;
      }

    }

    this.router.navigate([nextRoute]);
    return true;
  }
}
